import * as React from "react"
import { useQuery, useMutation, useLazyQuery } from "@apollo/client"
import { ErrorBoundary } from "react-error-boundary"
import { useRecoilState, useRecoilValue, useRecoilValueLoadable } from "recoil"
import { CoordinateSystemUnit, CreateProjectRequest, ProjectCreatedWithStatus, Point, PolicyType } from "../../types"
import {
  GET_CONFIG,
  HAS_ACCESS,
  CREATE_PROJECT,
  SYNC_PROJECT,
  ELIGIBLE_ACCOUNTS,
  GET_CRSUNIT,
  IS_ALLOWED_TO_ENABLE_AND_REMOVE_EXTENSION,
  GET_ALLOWED_PROJECT_REGIONS,
  GET_REGIONS_FROM_SETTINGS_API,
  GET_ACCOUNT_ADMINS,
  GET_GLOBAL_SETTINGS,
  HAS_PROJECT_ACCESS,
  HAS_PROJECT_OWNER_RIGHTS,
  IS_CUSTOMER_HANDLED_BY_TCPS
} from "../../api/QuadriApi"
import ConnectApi, { ConnectData } from "../../api/ConnectApi"
import {
  addSnackSelector,
  extensionApiState,
  projectIdState,
  projectOriginState,
  projectRegionState,
  quadriProjectIdState,
  signedInUserState,
  userProjectRoleState
} from "../StateSelectors"
import { PendingSpinner } from "../Pending"
import { WarningBanner, WarningTypes } from "./WarningBanner"
import { OopsFallback } from "../Oops/OopsFallback"
import ImportedProjectSettings from "./ImportedProjectSettings"
import ConnectProjectContext from "./ConnectProjectContext"
import QuadriProjectSettings from "./QuadriProjectSettings"
import QuadriHeader from "./QuadriHeader"
import QuadriFooter from "./QuadriFooter"
import { DeleteProject } from "./DeleteProject"
import Oops from "../Oops/Oops"
import { setMenu } from "../../util/setMenu"
import { useTranslation } from "react-i18next"
import { exceptionFromApolloError } from "../../util/apolloError"
import MissingLicense from "../MissingLicense"
import DeprecatedConfig from "../DeprecatedConfig"
import { SnackProps } from "./SnackBars"
import QuadriOwnershipSettings from "./QuadriOwnershipSettings"
import { track } from "../../util/gainsight"
import GetEnvironment from "../../environments"
import {
  checkIfAllowedToEnableInProjectRegion,
  checkIfAllowedToEnableOrRemoveExtension,
  getDisplayNamesForRegions
} from "./utils"
import { ExtensionRemovedMessage } from "./ExtensionRemovedMessage"
import PermissionsPanel from "./PermissionsPanel"
import { areasMatch, crsNamesAreEqual } from "../../util/projectSettings"
import { datesAreEqual } from "../../util/dateTools"

const BACKUP_RELOAD_WARNING = 10000
const BACKUP_RELOAD_TIMEOUT = 15000

// Used to determine the config page state. Exported so that the progress banner can use it
export type ConfigStatus = "Creating" | "Active" | "Failed" | "Updating" | "Default" | "Deleting"

interface ConfigMainProps {
  rights?: string[]
  forceProjectAccessQuery?: boolean // optional override for tests
  disableAutoSync?: boolean // optional flag to disable auto sync
}

// The exported wrapper component that handles error boundary and input
// to the loading component
const ConfigMain = ({ forceProjectAccessQuery, disableAutoSync }: ConfigMainProps) => {
  const projectId = useRecoilValue(projectIdState)
  const projectOrigin = useRecoilValue(projectOriginState)
  const signedInUser = useRecoilValue(signedInUserState)

  return (
    <ErrorBoundary FallbackComponent={OopsFallback}>
      <ConfigLoad
        projectId={projectId}
        projectOrigin={projectOrigin}
        signedInUser={signedInUser}
        forceProjectAccessQuery={forceProjectAccessQuery}
        disableAutoSync={disableAutoSync}
      />
    </ErrorBoundary>
  )
}

interface ConfigLoadProps {
  projectId?: string
  projectOrigin?: string
  accessToken?: string
  signedInUser?: string
  rights?: string[]
  forceProjectAccessQuery?: boolean
  disableAutoSync?: boolean
}

// TODO: Shorten this somehow
// The responsibility of this is to load everything necessary to render the
// Config page. It must therefore also hold initial project state as well
// as data from Connect
const ConfigLoad = (props: ConfigLoadProps) => {
  const { projectId, projectOrigin, signedInUser, rights } = props
  const { t } = useTranslation(["common", "config"])

  if (!projectId) throw new Error("Cannot load config page: Missing project id")
  if (!projectOrigin) throw new Error("Cannot load config page: Missing project origin")

  // Need to set this if this is the first time project is created
  const [, setQuadriProjectId] = useRecoilState(quadriProjectIdState)
  const extensionApi = useRecoilValue(extensionApiState)
  const [, setProjectRegionState] = useRecoilState(projectRegionState)
  const [projectStatus, setProjectStatus] = React.useState(undefined as ConfigStatus | undefined)

  // Connect Data
  const [connectApi, setConnectApi] = React.useState<ConnectApi | undefined>(undefined)
  const [connectData, setConnectData] = React.useState<ConnectData>({})
  const [fetchedConnect, setFetchedConnect] = React.useState(false)
  const [ConnectProjectOwnershipAccount, setConnectProjectOwnershipAccount] = React.useState("")
  const [ConnectProjectOwnershipAccountId, setConnectProjectOwnershipAccountId] = React.useState("")
  const [CrsName, SetCrsName] = React.useState<string>()

  // Feature Catalogs data depends on if the project has ever been created...
  const accessQuery = useQuery(HAS_ACCESS)

  const accountsQuery = useQuery(ELIGIBLE_ACCOUNTS)

  const crsUnitQuery = useQuery(GET_CRSUNIT, {
    variables: { crsName: CrsName ?? "" },
    skip: CrsName === undefined
  })

  const projectQuery = useQuery(GET_CONFIG, {
    variables: { id: projectId },
    onCompleted: (data) => {
      const updatedStatus = data?.tryGetConnectQuadriProject?.project?.projectStatus
      if (typeof updatedStatus !== "undefined") {
        setProjectStatus(updatedStatus)
        console.log("Setting project status: ", updatedStatus)
      }

      const quadriProjectId = data?.tryGetConnectQuadriProject?.project?.quadriProjectId
      if (quadriProjectId) {
        setQuadriProjectId(quadriProjectId)
        console.log("Set Quadri Project Id: ", quadriProjectId)
      }
    }
  })

  // Load role from async Recoil selector
  const projectUserRoleLoadable = useRecoilValueLoadable(userProjectRoleState)

  const [hasProjectAccess, setHasProjectAccess] = React.useState<boolean | undefined>(undefined)
  const [projectUserRole, setProjectUserRole] = React.useState<string | undefined>(undefined)

  const { state: roleState, contents: roleContents } = projectUserRoleLoadable

  // Handle error from Connect API (role fetch failure)
  // Let the ErrorBoundary handle role fetch failures

  if (roleState === "hasError") {
    throw roleContents
  }

  // Check if project is found from the GET_CONFIG query
  const projectFound = projectQuery.data?.tryGetConnectQuadriProject?.found

  // Only allow query if:
  // 1. The project exists OR forceProjectAccessQuery is enabled
  // 2. The role is resolved and is a valid non-empty string
  const roleIsReady = roleState === "hasValue" && typeof roleContents === "string" && roleContents.trim() !== ""
  const shouldSkipProjectAccess = !((projectFound || props.forceProjectAccessQuery) && roleIsReady)

  const projectAccessQuery = useQuery(HAS_PROJECT_ACCESS, {
    skip: shouldSkipProjectAccess,
    variables: {
      connectProjectId: projectId,
      connectProjectUserRole: roleIsReady ? roleContents : ""
    }
  })

  React.useEffect(() => {
    const hasAccess = projectAccessQuery.data?.hasProjectAccess.projectAccess
    const role = projectAccessQuery.data?.hasProjectAccess.role
    if (hasAccess !== undefined) {
      setHasProjectAccess(hasAccess)
      setProjectUserRole(role)
    }
  }, [projectAccessQuery.data, setHasProjectAccess, setProjectUserRole])

  const getGlobalSettingsQuery = useQuery(GET_GLOBAL_SETTINGS)

  const getRegionsQuery = useQuery(GET_REGIONS_FROM_SETTINGS_API)

  const { data } = projectQuery

  React.useEffect(() => {
    const runEffectAsync = async () => {
      const api = new ConnectApi(projectOrigin)
      setConnectApi(api)
      const connectData = await api.getConnectProjectData(projectId)
      console.log("Connect data: ", connectData)
      setConnectData(connectData)
      SetCrsName(connectData.crsName)
      setConnectProjectOwnershipAccount(connectData.connectProject?.license?.accounts[0]?.name)
      setConnectProjectOwnershipAccountId(connectData.connectProject?.license?.accounts[0]?.accountId)
      setProjectRegionState(connectData.region)
      setFetchedConnect(true)
    }
    runEffectAsync()
  }, [projectOrigin, projectId, setProjectRegionState])

  // Show the menu if the project is returned as active or if the object model config is enabled
  React.useEffect(() => {
    setMenu(t, projectStatus, extensionApi)
  }, [extensionApi, projectStatus, data, t])

  // "Handle" errors (they are handled by parent error boundary)
  if (projectAccessQuery.error) throw exceptionFromApolloError(projectAccessQuery.error)
  if (accessQuery.error) throw exceptionFromApolloError(accessQuery.error)
  if (accountsQuery.error) throw exceptionFromApolloError(accountsQuery.error)
  if (projectQuery.error) throw exceptionFromApolloError(projectQuery.error)
  if (crsUnitQuery.error) throw exceptionFromApolloError(crsUnitQuery.error)
  if (getGlobalSettingsQuery.error) throw exceptionFromApolloError(getGlobalSettingsQuery.error)
  if (getRegionsQuery.error) throw exceptionFromApolloError(getRegionsQuery.error)

  // From here, the queries are either loading, or they have data
  if (
    !connectApi ||
    !fetchedConnect ||
    projectUserRoleLoadable.state === "loading" ||
    (!shouldSkipProjectAccess && projectAccessQuery.loading) ||
    accessQuery.loading ||
    accountsQuery.loading ||
    projectQuery.loading ||
    crsUnitQuery.loading ||
    getGlobalSettingsQuery.loading ||
    getRegionsQuery.loading
  ) {
    return <PendingSpinner />
  } else {
    // Here, everything loaded successfully

    const needsLicense = !accessQuery.data.hasAccess?.access
    const noAccounts = !accountsQuery.data.eligibleAccountsWithLicenses
    const licensetype = accessQuery.data.hasAccess?.licenseType
    console.log("License type: ", licensetype)
    const { found, project } = projectQuery.data.tryGetConnectQuadriProject

    if (hasProjectAccess === false) {
      const noAccessMsg = t("error.NO_USER_ACCESS").replace("{{email}}", signedInUser!)
      return <Oops heading={t("error.heading.NO_USER_ACCESS")} body={noAccessMsg} isAuthError={true} />
    }

    if (!found && ["go", "pro"].includes(licensetype?.toLowerCase())) {
      // If this project is not TCPS licensed, we need to show the new config page
      return <DeprecatedConfig></DeprecatedConfig>
    }

    if (needsLicense) {
      return (
        <MissingLicense
          heading={t("licenseError.heading", { ns: "config" })}
          body1={t("licenseError.body1", { ns: "config" })}
          body2={t("licenseError.body2", { ns: "config" })}
          moreInfo={t("forMoreInfo.moreInfo", { ns: "common" })}
          quadriPlans={t("forMoreInfo.quadriPlans", { ns: "common" })}
          info={signedInUser}
          andCheck={t("forMoreInfo.andCheck", { ns: "common" })}
          subscriptions={t("forMoreInfo.subscriptions", { ns: "common" })}
        ></MissingLicense>
      )
    }
    if (noAccounts) {
      return (
        <MissingLicense
          heading={t("customerNotEligible.heading", { ns: "config" })}
          body1={t("customerNotEligible.body1", { ns: "config" })}
          body2={t("customerNotEligible.body2", { ns: "config" })}
          moreInfo={t("forMoreInfo.moreInfo", { ns: "common" })}
          quadriPlans={t("forMoreInfo.quadriPlans", { ns: "common" })}
          info={ConnectProjectOwnershipAccount}
          andCheck={t("forMoreInfo.andCheck", { ns: "common" })}
          subscriptions={t("forMoreInfo.subscriptions", { ns: "common" })}
        ></MissingLicense>
      )
    }

    let quadriConfigurationUiUrl
    if (getGlobalSettingsQuery.data) {
      quadriConfigurationUiUrl = getGlobalSettingsQuery.data.getGlobalSettings.quadriConfigurationFrontEndUrl
    }

    let regions
    if (getRegionsQuery.data) {
      regions = getRegionsQuery.data.getRegions.regions
    }

    //do we have crsUnit?
    let crsUnit
    if (crsUnitQuery.data) {
      crsUnit = crsUnitQuery.data.getCrsUnit.unitName
    }

    const innerProps: ConfigMainInnerProps = {
      connectProjectId: projectId,
      featureCatalogId: project?.featureCatalogId,
      extensionOwnershipId: project?.customerAccountId,
      extensionOwnershipName: project?.customerAccountName,
      extensionLicenseId: project?.productId,
      coordinateSystemUnit: crsUnit || project?.coordinateSystemUnit,
      coordinateSystemName: project?.coordinateSystemName,
      projectFound: found,
      connectData,
      projectStatus,
      projectStatusMessage: project?.projectStatusMessage,
      projectUpdateMessage: project?.projectUpdateMessage,
      projectName: project?.projectName,
      projectDescription: project?.projectDescription,
      projectStartDate: project?.projectStartDate,
      projectEndDate: project?.projectEndDate,
      createdAt: project?.createdAt,
      updatedAt: project?.updatedAt,
      permissionsUpdatedAt: project?.permissionsUpdatedAt,
      projectCreatedWithStatus: project?.projectCreatedWithStatus,
      tcpsArea: project?.projectArea,
      crsUnit,
      quadriConfigurationUiUrl,
      regions,
      setProjectStatus,
      setQuadriProjectId,
      rights,
      projectUserRole: projectUserRole || (roleIsReady ? (roleContents as string) : ""),
      licenseType: licensetype,
      isTcpsLicensed: project?.isTcpsLicensed,
      ConnectProjectOwnershipAccount: ConnectProjectOwnershipAccount,
      ConnectProjectOwnershipAccountId: ConnectProjectOwnershipAccountId,
      disableAutoSync: props.disableAutoSync,
      connectProjectOwnershipIdfromTCPS: project?.connectProjectOwnershipId,
      connectProjectOwnershipNamefromTCPS: project?.connectProjectOwnershipName
    }
    return <ConfigMainInner {...innerProps} />
  }
}

interface ConfigMainInnerProps {
  connectProjectId: string
  connectData: ConnectData
  featureCatalogId?: string
  extensionOwnershipId: string
  extensionOwnershipName: string
  extensionLicenseId: string
  coordinateSystemUnit?: CoordinateSystemUnit
  coordinateSystemName?: string
  projectFound?: boolean
  projectStatus?: ConfigStatus
  projectStatusMessage?: string
  projectUpdateMessage?: string
  projectName?: string
  projectDescription?: string
  projectStartDate?: string
  projectEndDate?: string
  createdAt?: string
  updatedAt?: string
  permissionsUpdatedAt?: string
  projectCreatedWithStatus?: ProjectCreatedWithStatus
  tcpsArea?: Point[]
  crsUnit: string | undefined
  quadriConfigurationUiUrl: string
  regions: any
  setProjectStatus: (status: ConfigStatus) => void
  setQuadriProjectId: (quadriProjectId: string) => void
  rights?: string[]
  projectUserRole: string
  licenseType: string
  isTcpsLicensed: boolean
  ConnectProjectOwnershipAccount: string
  ConnectProjectOwnershipAccountId: string
  disableAutoSync?: boolean
  connectProjectOwnershipIdfromTCPS?: string
  connectProjectOwnershipNamefromTCPS?: string
}

// This is the inner component responsible for page interaction and rendering
// It is assumed that all required data is loaded when this is rendered
const ConfigMainInner = (props: ConfigMainInnerProps) => {
  const { region, unitSystem, boundaryCoordinates, crsName, connectProject } = props.connectData
  const {
    projectStatus,
    projectStatusMessage,
    createdAt,
    updatedAt,
    permissionsUpdatedAt,
    connectProjectId,
    ConnectProjectOwnershipAccountId,
    ConnectProjectOwnershipAccount
  } = props
  const { setProjectStatus, setQuadriProjectId } = props
  const { projectUpdateMessage, projectCreatedWithStatus } = props
  const [, addSnack] = useRecoilState(addSnackSelector)
  const signedInUser = useRecoilValue(signedInUserState)
  const { t } = useTranslation("config")
  const [showPermissionsPanel, setShowPermissionsPanel] = React.useState(true)

  // Feature catalog state is kept here and not in child QuadriProjectSettings component
  // because it is used in the creation request that is tied to the button.
  const [extensionOwnership, setExtensionOwnership] = React.useState<string | undefined>(
    props.projectFound ? props.extensionOwnershipId : ConnectProjectOwnershipAccountId || undefined
  )
  const [extensionOwnershipName, setExtensionOwnershipName] = React.useState<string | undefined>(
    props.projectFound ? props.extensionOwnershipName : ConnectProjectOwnershipAccount
  )
  const [accountAdmins, setAccountAdmins] = React.useState([])
  const [featureCatalogId, setFeatureCatalogId] = React.useState<string | undefined>(props.featureCatalogId)
  const [featureCatalogStandard, setFeatureCatalogStandard] = React.useState<string | undefined>(undefined)
  const [coordinateSystemUnit, setCoordinateSystemUnit] = React.useState<CoordinateSystemUnit | undefined>(
    props.coordinateSystemUnit
  )
  const [showMessage, setShowMessage] = React.useState({
    show: false,
    for: "",
    accountName: extensionOwnershipName
  })

  const projectRegion = useRecoilValue(projectRegionState)
  const [isAllowedToEnableAndRemoveExtensionbyPolicy, setIsAllowedToEnableAndRemoveExtensionbyPolicy] =
    React.useState<boolean>(true)

  const [allowedByRegionPolicy, setAllowedByRegionPolicy] = React.useState<boolean>(true)
  const [allowedRegions, setAllowedRegions] = React.useState<string>("")

  const [settingsChanged, setSettingsChanged] = React.useState(false)
  const [syncStarted, setSyncStarted] = React.useState(false)

  // Use delete project hook for shared delete functionality
  const { buttonDeleteProject: onDisable, deleteModals } = DeleteProject({
    connectProjectId,
    projectStatus,
    isAllowedToEnableAndRemoveExtensionbyPolicy,
    extensionOwnershipName,
    accountAdmins,
    isTcpsLicensed: props.isTcpsLicensed
  })

  const isHandledByTcpsQuery = useQuery(IS_CUSTOMER_HANDLED_BY_TCPS, {
    variables: { customerAccountId: extensionOwnership ?? "" },
    skip: !extensionOwnership
  })

  const isHandledByTcps = isHandledByTcpsQuery.error
    ? false
    : isHandledByTcpsQuery.data?.isCustomerHandledByTcps?.isHandledByTcps

  const isHandledByTcpsLoading = !extensionOwnership ? false : isHandledByTcpsQuery.loading
  // Save the id of the existing feature catalog if there is one. This is used
  // to fetch a list of compatible feature catalogs if the user wants to upgrade
  const existingFcId = props.featureCatalogId || null
  const shouldSkipQuery =
    !GetEnvironment().enforceExtensionActivationAndRemovalPolicy ||
    extensionOwnership === undefined ||
    props.licenseType?.toLowerCase() === "quadribusiness"

  //Add query to get extension activation and removal policy
  const { loading, error, data } = useQuery(IS_ALLOWED_TO_ENABLE_AND_REMOVE_EXTENSION, {
    variables: { customerAccountId: extensionOwnership ?? "" },
    skip: shouldSkipQuery || isHandledByTcpsQuery.loading || isHandledByTcps === false
  })

  const {
    data: accountAdminsData,
    loading: loadingAdmins,
    error: errorAdmins
  } = useQuery(GET_ACCOUNT_ADMINS, {
    variables: { customerAccountId: extensionOwnership ?? "" },
    skip: shouldSkipQuery || isHandledByTcpsQuery.loading || isHandledByTcps === false
  })

  //Add query to get region policy
  const regionPolicyQuery = useQuery(GET_ALLOWED_PROJECT_REGIONS, {
    variables: { customerAccountId: extensionOwnership ?? "" },
    skip:
      !GetEnvironment().enforceRegionPolicy ||
      props.projectFound ||
      extensionOwnership === undefined ||
      isHandledByTcpsQuery.loading ||
      isHandledByTcps === false
  })

  const projectOwnerRightsQuery = useQuery(HAS_PROJECT_OWNER_RIGHTS, {
    variables: { connectProjectId: connectProjectId, usecases: ["Admin", "ClassicAdmin"], region: projectRegion }
  })

  let hasProjectOwnerClassicAdminRights: boolean = false
  if (projectOwnerRightsQuery.data) {
    const rights = projectOwnerRightsQuery.data.hasProjectOwnerRights?.rights || []
    const classicAdminRight = rights.find((r: any) => r.usecase === "ClassicAdmin")?.right ?? false
    hasProjectOwnerClassicAdminRights = classicAdminRight
  }

  React.useEffect(() => {
    if (!regionPolicyQuery.loading && !regionPolicyQuery.error && regionPolicyQuery.data) {
      const result = regionPolicyQuery.data?.getAllowedProjectRegions
      if (props.regions) {
        const regionsDisplayNames = props.regions.map((region: any) => {
          return { name: region.name, displayName: region.displayName }
        })
        setAllowedRegions(getDisplayNamesForRegions(result.regions, regionsDisplayNames))
      } else {
        setAllowedRegions(result.regions ? result.regions : "")
      }
      if (projectRegion) {
        setAllowedByRegionPolicy(checkIfAllowedToEnableInProjectRegion(result, projectRegion))
      }
    }
    if (regionPolicyQuery.error) {
      throw exceptionFromApolloError(regionPolicyQuery.error)
    }
  }, [regionPolicyQuery.loading, regionPolicyQuery.error, regionPolicyQuery.data, projectRegion, props.regions])

  React.useEffect(() => {
    if (!loading && !error && data && accountAdminsData) {
      setIsAllowedToEnableAndRemoveExtensionbyPolicy(checkIfAllowedToEnableOrRemoveExtension(data))
      setAccountAdmins(accountAdminsData.getAccountAdmins)
    }
    if (error) {
      throw exceptionFromApolloError(error)
    }

    if (errorAdmins) {
      throw exceptionFromApolloError(errorAdmins)
    }
  }, [loading, error, data, accountAdminsData, errorAdmins])

  // If project is never created, then this is the mutation that gets sent on button click
  const [createProject, createProjectResponse] = useMutation(CREATE_PROJECT, {
    onCompleted: (data: any) => {
      console.log("On create complete, data: ", data)
      const quadriProjectId = data?.createProject?.projectId
      if (typeof quadriProjectId !== "undefined") {
        setQuadriProjectId(quadriProjectId)
      }
      startBackupTimeoutReload(t, addSnack)
    },
    onError: (_error) => setProjectStatus("Failed")
  })

  // Mutation used for syncing after the project has been created
  const [syncProject, syncProjectResponse] = useMutation(SYNC_PROJECT, {
    onCompleted: (data: any) => {
      startBackupTimeoutReload(t, addSnack)
      setSyncStarted(false)
    }
  })

  const closeMessageModal = () => {
    setShowMessage({ show: false, for: "", accountName: "" })
  }
  const submitQuadriProject = React.useCallback(
    (e?: any) => {
      if (syncStarted) {
        return
      }
      setSyncStarted(true)
      e?.preventDefault()

      if (!props.projectFound && !isAllowedToEnableAndRemoveExtensionbyPolicy) {
        setShowMessage({
          show: true,
          for: PolicyType.extensionActivationAndRemovalPolicy,
          accountName: extensionOwnershipName
        })
        return
      } else if (!allowedByRegionPolicy) {
        setShowMessage({ show: true, for: PolicyType.regionPolicy, accountName: extensionOwnershipName })
        return
      }

      if (
        connectProjectId !== undefined &&
        featureCatalogId !== undefined &&
        coordinateSystemUnit !== undefined &&
        region !== undefined &&
        ConnectProjectOwnershipAccountId !== undefined
      ) {
        const project = {
          connectProjectId: connectProjectId,
          featureCatalogId,
          coordinateSystemUnit,
          connectProjectRegion: region,
          customerAccountId: ConnectProjectOwnershipAccountId, // Use the project ownership from Connect instead of the extension ownership
          isTcpsLicensed: projectStatus === "Active" ? props.isTcpsLicensed : hasProjectOwnerClassicAdminRights
        }

        if (projectStatus === "Active") {
          track("project_update", { connectProjectId: connectProjectId })
          const syncProjectRequest = { ...project } as any
          syncProjectRequest.extensionOwnership = extensionOwnership
          if (extensionOwnership === props.connectProjectOwnershipIdfromTCPS) {
            delete syncProjectRequest.extensionOwnership
          }
          setProjectStatus("Updating")
          syncProject({ variables: { project: syncProjectRequest } }).catch((error) => {
            addSnack({
              type: "error",
              message: error.message ?? "Failed to update the project",
              duration: 3000
            })
            setProjectStatus("Active")
            setSyncStarted(false)
          })
        } else {
          track("project_create", { connectProjectId: connectProjectId })
          setProjectStatus("Creating")
          createProject({ variables: { project: project } })
        }
      } else {
        console.error("Missing create/update parameters")
      }
    },
    [
      syncStarted,
      props.projectFound,
      isAllowedToEnableAndRemoveExtensionbyPolicy,
      allowedByRegionPolicy,
      connectProjectId,
      featureCatalogId,
      coordinateSystemUnit,
      region,
      ConnectProjectOwnershipAccountId,
      projectStatus,
      props.isTcpsLicensed,
      hasProjectOwnerClassicAdminRights,
      extensionOwnership,
      extensionOwnershipName,
      props.connectProjectOwnershipIdfromTCPS,
      setProjectStatus,
      syncProject,
      createProject,
      addSnack
    ]
  )
  // --- mismatch checks ---
  const stringsMatch = (a: string | undefined, b: string | undefined) => {
    return (a || "").trim() === (b || "").trim()
  }

  const areaMismatch = !areasMatch(props.connectData.boundaryCoordinates, props.tcpsArea || [])
  const crsNameMismatch = !crsNamesAreEqual(props.connectData.crsName, props.coordinateSystemName)
  const projectNameMismatch = !stringsMatch(props.projectName, props.connectData.connectProject?.name)
  const projectDescriptionMismatch = !stringsMatch(
    props.projectDescription,
    props.connectData.connectProject?.description
  )
  const projectStartDateMismatch = !datesAreEqual(props.projectStartDate, props.connectData.connectProject?.startDate)
  const projectEndDateMismatch = !datesAreEqual(props.projectEndDate, props.connectData.connectProject?.endDate)
  const projectOwnershipMismatch = props.connectProjectOwnershipIdfromTCPS !== ConnectProjectOwnershipAccountId

  const needSync =
    areaMismatch ||
    crsNameMismatch ||
    projectNameMismatch ||
    projectDescriptionMismatch ||
    projectStartDateMismatch ||
    projectEndDateMismatch ||
    projectOwnershipMismatch

  const configStatus = projectStatus || "Default"
  const changeInProgress = configStatus === "Creating" || configStatus === "Updating" || configStatus === "Deleting"

  // --- auto-sync effect ---
  React.useEffect(() => {
    if (!props.disableAutoSync) {
      if (
        needSync &&
        !changeInProgress &&
        props.projectUserRole === "ADMIN" &&
        projectStatus === "Active" &&
        !syncStarted
      ) {
        console.warn("Project config is not in sync between Connect and TCPS")
        submitQuadriProject()
      }
    }
  }, [
    needSync,
    changeInProgress,
    props.projectUserRole,
    projectStatus,
    syncStarted,
    submitQuadriProject,
    props.disableAutoSync
  ])

  // Disable creation
  const isEnableButtonDisabled =
    changeInProgress || !featureCatalogId || !coordinateSystemUnit || !extensionOwnership || !settingsChanged

  const regionSupported = props.regions.some((regionObj: { name: string }) => regionObj.name === region)
  console.log("Region supported: ", regionSupported)

  console.log("User rights: ", props.rights)
  console.log("Connect project user role: ", props.projectUserRole)

  const editorLicenseAndAdmin =
    props.projectUserRole === "ADMIN" &&
    (props.rights?.includes("Write") ||
      ["quadribusinesspremium", "innovate"].includes(props.licenseType?.toLowerCase()))

  // Disable editing
  const disableEdit = changeInProgress || !regionSupported || !editorLicenseAndAdmin
  const disableEditCoordinateSystemUnit = disableEdit || !!props.crsUnit
  const quadriProjectId = useRecoilValue(quadriProjectIdState)
  const extensionRemoved = localStorage.getItem("extensionRemoved")
  console.log("Extension removed: ", extensionRemoved)
  const projectFoundWithSplitOwnership =
    props.projectFound && extensionOwnership !== props.connectProjectOwnershipIdfromTCPS

  return (
    <>
      {extensionRemoved === "true" ? (
        <ExtensionRemovedMessage projectId={connectProjectId} />
      ) : (
        <div>
          <div style={{ paddingLeft: "16px", paddingRight: "16px" }}>
            {regionSupported ? null : <WarningBanner warningType={WarningTypes.Region} />}
            <div className="row align-items-stretch" style={{ minHeight: "100vh" }}>
              {/* Left side (main content) */}
              <div className={showPermissionsPanel && props.projectFound ? "col-9" : "col-12"}>
                {deleteModals}
                <QuadriHeader
                  configStatus={configStatus}
                  isEnableButtonDisabled={isEnableButtonDisabled}
                  isEnableButtonHidden={!editorLicenseAndAdmin}
                  statusMessage={projectStatusMessage}
                  updateErrorMessage={projectUpdateMessage}
                  createProjectResponse={createProjectResponse}
                  submitQuadriProject={submitQuadriProject}
                  createdAt={createdAt}
                  updatedAt={updatedAt}
                  permissionsUpdatedAt={permissionsUpdatedAt}
                  projectCreatedWithStatus={projectCreatedWithStatus}
                  showMessage={showMessage}
                  closeMessageModal={closeMessageModal}
                  allowedRegions={allowedRegions}
                  accountAdmins={accountAdmins}
                  openPanel={() => setShowPermissionsPanel(true)}
                  closePanel={() => setShowPermissionsPanel(false)}
                  showPermissionsPanel={showPermissionsPanel}
                  onDisable={onDisable}
                />
                {!props.projectFound || // always render if project not found
                projectFoundWithSplitOwnership ? ( // render if found but ownership differs
                  <QuadriOwnershipSettings
                    extensionOwnership={extensionOwnership}
                    extensionOwnershipName={extensionOwnershipName}
                    setExtensionOwnership={setExtensionOwnership}
                    setExtensionOwnershipName={setExtensionOwnershipName}
                    disableEdit={props.projectFound ? disableEdit : true}
                    onChange={() => setSettingsChanged(true)}
                    projectFoundWithSplitOwnership={projectFoundWithSplitOwnership}
                  />
                ) : null}
                <QuadriProjectSettings
                  existingFcId={existingFcId}
                  fcStandard={featureCatalogStandard}
                  setFcStandard={setFeatureCatalogStandard}
                  featureCatalogId={featureCatalogId}
                  setFeatureCatalogId={setFeatureCatalogId}
                  coordinateSystemUnits={coordinateSystemUnit}
                  setCoordinateSystemUnits={setCoordinateSystemUnit}
                  disableEdit={disableEdit}
                  disableEditCoordinateSystemUnit={disableEditCoordinateSystemUnit}
                  configStatus={configStatus}
                  projectStatus={projectStatus}
                  region={region}
                  quadriConfigurationUiUrl={props.quadriConfigurationUiUrl}
                  onChange={() => setSettingsChanged(true)}
                  connectProjectId={props.connectProjectId}
                />
                <ImportedProjectSettings
                  connectProjectId={props.connectProjectId}
                  unitSystem={unitSystem}
                  boundaryCoordinates={boundaryCoordinates}
                  csib64={connectProject?.crs?.csib64}
                  crsName={crsName}
                  tcpsArea={props.tcpsArea}
                  tcpsCrsName={props.coordinateSystemName}
                  projectEnabled={projectStatus === "Active"}
                  submitQuadriProject={submitQuadriProject}
                  disableEdit={disableEdit}
                />
                {props.connectData.connectProject && (
                  <ConnectProjectContext
                    region={props.connectData.region}
                    description={props.connectData.connectProject.description}
                    name={props.connectData.connectProject.name}
                    startDate={props.connectData.connectProject.startDate}
                    endDate={props.connectData.connectProject.endDate}
                  />
                )}
                <QuadriFooter
                  connectProjectId={connectProjectId}
                  projectStatus={projectStatus}
                  changeInProgress={changeInProgress}
                  isAllowedToEnableAndRemoveExtensionbyPolicy={isAllowedToEnableAndRemoveExtensionbyPolicy}
                  extensionOwnershipName={extensionOwnershipName}
                  accountAdmins={accountAdmins}
                  isTcpsLicensed={props.isTcpsLicensed}
                  isRemoveButtonHidden={!editorLicenseAndAdmin}
                  configStatus={configStatus}
                  isEnableButtonDisabled={isEnableButtonDisabled}
                  isEnableButtonHidden={!editorLicenseAndAdmin}
                  submitQuadriProject={submitQuadriProject}
                />
              </div>

              {/* Right side (permissions panel) */}
              {showPermissionsPanel && props.projectFound && (
                <PermissionsPanel
                  isEnableButtonHidden={!editorLicenseAndAdmin}
                  onClose={() => setShowPermissionsPanel(false)}
                  quadriProjectId={quadriProjectId}
                  connectProjectId={connectProjectId}
                  permissionsUpdatedAt={permissionsUpdatedAt}
                  disabled={
                    changeInProgress ||
                    !featureCatalogId ||
                    !coordinateSystemUnit ||
                    !extensionOwnership ||
                    configStatus !== "Active"
                  }
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export const startBackupTimeoutReload = (t: any, addSnack: (props: SnackProps) => void) => {
  const snackTimer = setTimeout(() => {
    console.log(`Warning snackbar since no kafka-notification was received after ${BACKUP_RELOAD_WARNING} ms`)
    addSnack(getBackupReloadWarnSnackProps(t, BACKUP_RELOAD_WARNING))
  }, BACKUP_RELOAD_WARNING)
  clearTimeout(snackTimer)
  const reloadtimer = setTimeout(() => {
    console.log(`Reloading page since no kafka-notification was received after ${BACKUP_RELOAD_TIMEOUT} ms`)
    window.location.reload()
  }, BACKUP_RELOAD_TIMEOUT)
  clearTimeout(reloadtimer)
}

const getBackupReloadWarnSnackProps = (t: any, timeMs: number): SnackProps => ({
  type: "warning",
  message: t("backupReloadWarning.warning")
})

export default ConfigMain
